import { useEffect, useRef, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import AddButton from "@/components/Core/AddButton";
import AppAlertDialog from "@/components/Core/AppAlertDialog/AppAlertDialog";
import AppInputNew from "@/components/Core/AppInput/AppInputNew";
import TextAreaNew from "@/components/Core/TextArea/TextAreaNew";
import Subsidiary, { SubsidiaryAddress } from "@/models/Subsidiary";
import { useOrganisation } from "@/networking/organisation.hooks";
import {
	useCreateSubsidiary,
	useDeleteSubsidiary,
	useSubsidiaries,
	useSubsidiary,
	useUpdateSubsidiary,
} from "@/networking/subsidiary.hooks";
import { toast } from "@/utils/toast";
import { EmploymentContractPreviewButton } from "./EmploymentContractPreview";
import { SettingsLeaf } from "./SettingsLeafLayout";
import { primaryButtonClasses, secondaryButtonClasses } from "./TaxonomyCrudEditor";

/**
 * "Dotterbolag" — CRUD over the organisation's subsidiaries. Mirrors the
 * taxonomy-editor look (row list + inline gray-box form) but with a richer
 * form, so it is hand-rolled from the same building blocks rather than
 * reusing {@link TaxonomyCrudEditor}.
 */
const DotterbolagPage = () => {
	const { t } = useTranslation();
	const { subsidiaries, isLoading } = useSubsidiaries();
	const { mutateAsync: deleteSubsidiary, isLoading: isDeleting } = useDeleteSubsidiary();

	// `editingId === "new"` → create form at the bottom, otherwise an inline
	// edit form replaces the matching row.
	const [editingId, setEditingId] = useState<string | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<Subsidiary | null>(null);

	const handleConfirmDelete = async () => {
		if (!deleteTarget) return;
		try {
			await deleteSubsidiary(deleteTarget.id);
			toast.success(t("organisation.subsidiaries.deleted"));
		} catch {
			// Deletion is blocked while consultants/customers reference the subsidiary.
			toast.error(t("organisation.subsidiaries.deleteBlocked"));
		}
	};

	const renderViewRow = (subsidiary: Subsidiary) => (
		<div
			key={subsidiary.id}
			className="group flex items-center justify-between gap-3 border-t border-gray-200 py-2.5 first:border-t-0 first:pt-0"
		>
			<div className="min-w-0">
				<span className="text-sm font-medium text-gray-900">{subsidiary.name}</span>
				{(subsidiary.orgNo || subsidiary.address?.city) && (
					<span className="ml-3 text-xs text-gray-500">
						{[subsidiary.orgNo, subsidiary.address?.city].filter(Boolean).join(" · ")}
					</span>
				)}
				{/* Short id (first UUID segment) for cross-referencing; full id on hover. */}
				<div className="mt-1">
					<span
						title={subsidiary.id}
						className="inline-block select-all rounded-sm bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] leading-none text-gray-500"
					>
						{subsidiary.id.slice(0, 8)}
					</span>
				</div>
			</div>
			<div className="flex shrink-0 items-center gap-1">
				<button
					type="button"
					onClick={() => setEditingId(subsidiary.id)}
					disabled={isDeleting}
					aria-label="Redigera"
					title="Redigera"
					className="flex h-8 w-8 items-center justify-center rounded-sm border border-transparent text-gray-400 transition-colors hover:border-gray-200 hover:bg-gray-50 hover:text-gray-700"
				>
					<Pencil size={16} />
				</button>
				<button
					type="button"
					onClick={() => setDeleteTarget(subsidiary)}
					disabled={isDeleting}
					aria-label="Ta bort"
					title="Ta bort"
					className="flex h-8 w-8 items-center justify-center rounded-sm border border-transparent text-gray-400 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
				>
					<Trash2 size={16} />
				</button>
			</div>
		</div>
	);

	return (
		<SettingsLeaf
			title={t("organisation.subsidiaries.title")}
			description={t("organisation.subsidiaries.description")}
		>
			{!isLoading && subsidiaries.length === 0 ? (
				<p className="text-sm text-gray-500">{t("organisation.subsidiaries.empty")}</p>
			) : (
				<div>
					{subsidiaries.map((subsidiary) =>
						subsidiary.id === editingId ? (
							<div
								key={subsidiary.id}
								className="border-t border-gray-200 py-2.5 first:border-t-0 first:pt-0"
							>
								<SubsidiaryForm subsidiaryId={subsidiary.id} onClose={() => setEditingId(null)} />
							</div>
						) : (
							renderViewRow(subsidiary)
						)
					)}
				</div>
			)}

			<div className="border-t border-gray-200 pt-4">
				{editingId === "new" ? (
					<SubsidiaryForm onClose={() => setEditingId(null)} />
				) : (
					<AddButton
						label={t("organisation.subsidiaries.add")}
						onClick={() => setEditingId("new")}
					/>
				)}
			</div>

			<AppAlertDialog
				open={!!deleteTarget}
				onOpenChange={(open) => {
					if (!open) setDeleteTarget(null);
				}}
				variant="danger"
				skipTranslation
				title={deleteTarget ? `Ta bort dotterbolaget "${deleteTarget.name}"?` : ""}
				description={t("organisation.subsidiaries.deleteConfirm")}
				confirmLabel={t("DELETE")}
				cancelLabel={t("CANCEL")}
				onConfirm={handleConfirmDelete}
			/>
		</SettingsLeaf>
	);
};

export default DotterbolagPage;

interface SubsidiaryFormProps {
	/** Set when editing an existing subsidiary; omitted when creating. */
	subsidiaryId?: string;
	onClose: () => void;
}

/** Inline create/edit form in the gray-box style of the taxonomy editors. */
const SubsidiaryForm = ({ subsidiaryId, onClose }: SubsidiaryFormProps) => {
	const { t } = useTranslation();
	const isEdit = !!subsidiaryId;

	const { data: organisation } = useOrganisation();
	// The list omits recruitmentContract, so fetch the full subsidiary when editing.
	const { subsidiary, isLoading } = useSubsidiary(subsidiaryId, isEdit);
	const { mutateAsync: createSubsidiary, isLoading: isCreating } = useCreateSubsidiary();
	const { mutateAsync: updateSubsidiary, isLoading: isUpdating } = useUpdateSubsidiary();
	const isSaving = isCreating || isUpdating;

	const [name, setName] = useState("");
	const [orgNo, setOrgNo] = useState("");
	const [address, setAddress] = useState<SubsidiaryAddress>({});
	const [cfar, setCfar] = useState("");
	const [recruitmentContract, setRecruitmentContract] = useState("");
	// No UI for pools yet — the stored value is carried through unchanged on save.
	const [pools, setPools] = useState<string[]>([]);

	useEffect(() => {
		if (subsidiary) {
			setName(subsidiary.name || "");
			setOrgNo(subsidiary.orgNo || "");
			setAddress(subsidiary.address || {});
			setCfar(subsidiary.cfar || "");
			setRecruitmentContract(subsidiary.recruitmentContract || "");
			setPools(subsidiary.pools || []);
		}
	}, [subsidiary]);

	const nameInputRef = useRef<HTMLInputElement>(null);
	useEffect(() => {
		if (!isEdit) nameInputRef.current?.focus();
	}, [isEdit]);

	function setAddressField(field: keyof SubsidiaryAddress, value: string) {
		setAddress((prev) => ({ ...prev, [field]: value }));
	}

	async function handleSave() {
		const body = {
			name: name.trim(),
			orgNo: orgNo.trim() || undefined,
			address: Object.values(address).some((v) => v && v.trim()) ? address : undefined,
			cfar: cfar.trim() || undefined,
			recruitmentContract: recruitmentContract.trim() || undefined,
			pools,
		};

		if (!body.name) return;

		if (isEdit && subsidiaryId) {
			await updateSubsidiary({ subsidiaryId, update: body });
		} else {
			await createSubsidiary(body);
		}
		onClose();
	}

	if (isEdit && isLoading) {
		return (
			<div className="rounded-md border border-gray-200 bg-gray-50/50 p-3.5">
				<p className="text-sm text-gray-500">...</p>
			</div>
		);
	}

	return (
		<div className="space-y-5 rounded-md border border-gray-200 bg-gray-50/50 p-3.5">
			<div className="flex flex-wrap items-start gap-2">
				<AppInputNew
					ref={nameInputRef}
					containerClassName="!w-auto flex-1 min-w-[180px]"
					label="organisation.subsidiaries.name"
					value={name}
					onChangeText={setName}
					onEnter={handleSave}
				/>
				<AppInputNew
					containerClassName="!w-[180px]"
					label="organisation.subsidiaries.orgNo"
					value={orgNo}
					onChangeText={setOrgNo}
				/>
			</div>

			<AppInputNew
				label="organisation.subsidiaries.address1"
				value={address.address1 || ""}
				onChangeText={(v) => setAddressField("address1", v)}
			/>
			<div className="flex flex-wrap items-start gap-2">
				<AppInputNew
					containerClassName="!w-auto flex-1 min-w-[120px]"
					label="organisation.subsidiaries.careOf"
					value={address.careOf || ""}
					onChangeText={(v) => setAddressField("careOf", v)}
				/>
				<AppInputNew
					containerClassName="!w-[140px]"
					label="organisation.subsidiaries.postalCode"
					value={address.postalCode || ""}
					onChangeText={(v) => setAddressField("postalCode", v)}
				/>
				<AppInputNew
					containerClassName="!w-auto flex-1 min-w-[140px]"
					label="organisation.subsidiaries.city"
					value={address.city || ""}
					onChangeText={(v) => setAddressField("city", v)}
				/>
			</div>

			<AppInputNew
				containerClassName="!w-[220px]"
				label="organisation.subsidiaries.cfar"
				value={cfar}
				onChangeText={setCfar}
				description={t("organisation.subsidiaries.cfarDescription")}
			/>

			<div>
				<TextAreaNew
					label="organisation.subsidiaries.recruitmentContract"
					description={t("organisation.subsidiaries.recruitmentContractDescription")}
					height="20vh"
					value={recruitmentContract}
					onChangeText={setRecruitmentContract}
				/>
				<div className="mt-2">
					{/* {{organisation.*}} resolves to the subsidiary's identity with
					    per-field fall-through to the organisation, like the backend. */}
					<EmploymentContractPreviewButton
						template={recruitmentContract}
						organisation={{
							...organisation,
							...(name.trim() ? { name: name.trim() } : {}),
							...(orgNo.trim() ? { orgNo: orgNo.trim() } : {}),
							...(Object.values(address).some((v) => v && v.trim()) ? { address } : {}),
						}}
					/>
				</div>
			</div>

			<div className="flex items-center gap-2 border-t pt-4">
				<button
					type="button"
					onClick={handleSave}
					disabled={!name.trim() || isSaving}
					className={primaryButtonClasses}
				>
					{t("SAVE")}
				</button>
				<button
					type="button"
					onClick={onClose}
					disabled={isSaving}
					className={secondaryButtonClasses}
				>
					{t("CANCEL")}
				</button>
			</div>
		</div>
	);
};
